import { useEffect, useState } from "react";
import { Lock, LogOut, Paperclip, RefreshCw } from "lucide-react";
import { Logo } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem("hadron-admin") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [items, setItems] = useState(null);
  const [subs, setSubs] = useState(null);
  const [view, setView] = useState("inquiries");
  const [openMsg, setOpenMsg] = useState(-1);

  const load = async (tok) => {
    const r = await fetch(`${API}/inquiries`, { headers: { Authorization: `Bearer ${tok}` } });
    if (r.status === 401) {
      sessionStorage.removeItem("hadron-admin");
      setToken("");
      setError("Session expired — log in again.");
      return;
    }
    setItems(await r.json());
    const r2 = await fetch(`${API}/newsletter`, { headers: { Authorization: `Bearer ${tok}` } });
    if (r2.ok) setSubs(await r2.json());
  };

  useEffect(() => {
    if (token) load(token);
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    const r = await fetch(`${API}/admin/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      setError(d.detail === "locked" ? "Too many attempts — try again in 15 minutes." : "Wrong password.");
      return;
    }
    const data = await r.json();
    sessionStorage.setItem("hadron-admin", data.token);
    setToken(data.token);
    setPassword("");
    load(data.token);
  };

  const logout = () => {
    sessionStorage.removeItem("hadron-admin");
    setToken("");
    setItems(null);
    setSubs(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-ink grid place-items-center px-4">
        <form onSubmit={login} className="card-surface p-8 sm:p-10 w-full max-w-md" data-testid="admin-login-form">
          <Logo />
          <p className="mt-8 mono-label !text-[9px]">Admin — Inquiry Inbox</p>
          <input
            data-testid="admin-login-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="w-full mt-3 bg-ink border border-line px-4 py-3.5 text-sm text-txt placeholder:text-txt-3 focus:outline-none focus:border-hadron-cyan"
          />
          {error && <p className="mt-3 text-xs text-rose-400" data-testid="admin-login-error">{error}</p>}
          <button data-testid="admin-login-button" className="mt-5 w-full bg-hadron-cyan clip-frame px-6 py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] text-ink hover:bg-hadron-green transition-colors">
            Log in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-txt font-body">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <Logo />
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={() => load(token)} className="inline-flex items-center gap-2 border border-line px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest text-txt-2 hover:text-hadron-cyan hover:border-hadron-cyan transition-colors">
              <RefreshCw size={12} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 border border-line px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest text-txt-2 hover:text-rose-400 hover:border-rose-400 transition-colors">
              <LogOut size={12} /> Log out
            </button>
          </div>
        </div>

        <div className="mt-10 flex items-center gap-3 flex-wrap">
          {[
            ["inquiries", "Inquiries", items],
            ["newsletter", "Newsletter", subs],
          ].map(([key, label, list]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setView(key)}
              className={`px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest border transition-colors ${view === key ? "border-hadron-cyan text-hadron-cyan bg-hadron-cyan/10" : "border-line text-txt-2 hover:text-hadron-cyan hover:border-hadron-cyan"}`}
            >
              {label} <span className="opacity-70">({list ? list.length : "…"})</span>
            </button>
          ))}
        </div>

        {view === "inquiries" && (
          <>
            {items && items.length === 0 && <p className="mt-6 text-txt-3 text-sm">No inquiries yet.</p>}

            <div className="mt-6 space-y-3" data-testid="admin-inquiry-list">
              {(items || []).map((it, i) => (
                <div key={it.id || i} className="card-surface p-5" data-testid={`admin-row-${i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="font-medium text-sm">
                      {it.name} {it.company && <span className="text-txt-3">· {it.company}</span>}
                    </p>
                    <p className="font-mono2 text-[10px] uppercase tracking-widest text-txt-3">{(it.created_at || "").slice(0, 16).replace("T", " ")} UTC</p>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-txt-2">
                    <a href={`mailto:${it.email}`} className="hover:text-hadron-cyan">{it.email}</a>
                    <span className="text-txt-3">{it.product_interest}</span>
                    <span className="border border-line px-2 py-0.5 font-mono2 text-[9px] uppercase">{it.language}</span>
                    {it.attachment_name && (
                      <a data-testid={`admin-attachment-${i}`} href={`${API}/files/${it.attachment_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-hadron-cyan hover:underline">
                        <Paperclip size={11} /> {it.attachment_name} ({Math.max(1, (it.attachment_size || 0) >> 10)} KB)
                      </a>
                    )}
                  </div>
                  <button onClick={() => setOpenMsg(openMsg === i ? -1 : i)} className="mt-2.5 font-mono2 text-[10px] uppercase tracking-widest text-txt-3 hover:text-hadron-cyan">
                    {openMsg === i ? "− Hide message" : "+ Show message"}
                  </button>
                  {openMsg === i && <p className="mt-2 text-sm text-txt-2 leading-relaxed border-l-2 border-hadron-cyan/40 pl-3">{it.message}</p>}
                </div>
              ))}
            </div>
          </>
        )}

        {view === "newsletter" && (
          <>
            {subs && subs.length === 0 && <p className="mt-6 text-txt-3 text-sm">No subscribers yet.</p>}

            <div className="mt-6 space-y-3" data-testid="admin-subscriber-list">
              {(subs || []).map((s, i) => (
                <div key={s.id || i} className="card-surface p-5" data-testid={`admin-sub-row-${i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <a href={`mailto:${s.email}`} className="font-medium text-sm hover:text-hadron-cyan">{s.email}</a>
                    <p className="font-mono2 text-[10px] uppercase tracking-widest text-txt-3">{(s.created_at || "").slice(0, 16).replace("T", " ")} UTC</p>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-txt-2">
                    <span className="border border-line px-2 py-0.5 font-mono2 text-[9px] uppercase">{s.language}</span>
                    <span className="text-txt-3">source: {s.source}</span>
                    {s.topic && <span className="text-txt-3">{s.topic}</span>}
                    <span className="text-txt-3">consent: {s.consent_text}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <p className="mt-10 font-mono2 text-[10px] uppercase tracking-widest text-txt-3 flex items-center gap-2">
          <Lock size={11} /> Hadron Energy Poland — internal
        </p>
      </div>
    </div>
  );
};
